import type { Font as _Font } from '../../../typography/utils.js';
import { FONT_BASE } from '../constants.js';
import type { Dimensions } from '../types/dimensions.js';

/** Creates font object for `getTextWidth()`. */
const getFont = (
  fontStyle: string,
  fontWeight: string,
  fontFamily: string,
) => ({
  fontStyle: fontStyle,
  fontWeight: fontWeight,
  fontSize: `${FONT_BASE}px`,
  fontFamily: fontFamily,
});

/**
 * Transforms a font settings object into a CSS font specifier string
 * that can be used on 2D canvas contexts.
 */
const getFontString = (textFont: _Font): string => {
  const { fontStyle, fontWeight, fontSize, fontFamily } = textFont;

  return [fontStyle, fontWeight, fontSize, fontFamily]
    .filter((value) => !!value)
    .join(' ');
};

const getTextBBox = (text: string, textFont: _Font): Dimensions | undefined => {
  const context = document.createElement('canvas').getContext('2d');

  if (context) {
    context.imageSmoothingEnabled = false;
    context.imageSmoothingQuality = 'low';

    context.font = getFontString(textFont);
    const textMetrics = context.measureText(text);

    // more accurate than using metrics width
    // see https://developer.mozilla.org/en-US/docs/Web/API/TextMetrics#measuring_text_width
    const actualTextWidth =
      Math.abs(textMetrics.actualBoundingBoxLeft) +
      Math.abs(textMetrics.actualBoundingBoxRight);

    const actualTextHeight =
      Math.abs(textMetrics.actualBoundingBoxAscent) +
      Math.abs(textMetrics.actualBoundingBoxDescent);

    // Scrap sub-pixel accuracy and always round up to be on the safer side
    return {
      width: Math.ceil(actualTextWidth),
      height: Math.ceil(actualTextHeight),
    };
  }
};

/** A function that returns the size of a given text with font styles applied in fontsize 10 */
export const measureFontBaseline = ({
  text,
  fontStyle,
  fontWeight,
  fontFamily,
}: {
  fontStyle: string;
  fontWeight: string;
  fontFamily: string;
  text: string;
}) => {
  const font = getFont(fontStyle, fontWeight, fontFamily);

  return getTextBBox(text, font);
};
